import { dark } from '@clerk/themes'
import type { ClerkProviderProps } from '@clerk/clerk-react'

export type OrigensColorScheme = 'light' | 'dark'
export type OrigensClerkAppearance = NonNullable<ClerkProviderProps['appearance']>

/**
 * Literal hex values mirroring `packages/ui` tokens.css.
 *
 * IMPORTANT: never pass the app's CSS custom properties (e.g. `var(--accent)`,
 * `var(--ring)`) into Clerk `variables`. Clerk's runtime emits its own custom
 * properties with the SAME names (`--accent`, `--accentContrast`, `--ring`,
 * `--border`, `--muted`, ...), so a value like `var(--accent)` becomes the
 * circular definition `--accent: var(--accent)` — an invalid property that
 * left primary buttons with a transparent background in dark mode.
 */
const palette = {
  light: {
    accent: '#0879ff',
    accentFg: '#ffffff',
    ink: '#142033',
    inkMuted: '#5c6b80',
    surface: '#f4f6fa',
    card: '#ffffff',
    muted: '#e8edf5',
    border: '#d0d8e6',
    ring: '#0879ff',
    destructive: '#b54a3f',
  },
  dark: {
    accent: '#4d9fff',
    // White on accent reads clearly; dark navy (--accent-fg) was unreadable
    // on the Invite / Send invitations buttons.
    accentFg: '#ffffff',
    ink: '#e7e4db',
    inkMuted: '#97948a',
    surface: '#0b0c0d',
    card: '#121417',
    muted: '#1a1d21',
    border: '#2a2f36',
    ring: '#4d9fff',
    destructive: '#d46a5e',
  },
} as const

/**
 * Shared Clerk appearance keyed to Origens design tokens (`packages/ui` tokens.css).
 * Pass the ThemeProvider-resolved scheme so modals track light/dark toggles.
 */
export function origensClerkAppearance(colorScheme: OrigensColorScheme): OrigensClerkAppearance {
  const colors = palette[colorScheme]

  const variables: NonNullable<OrigensClerkAppearance['variables']> = {
    // Hex only (see palette note): Clerk derives alpha/hover scales from these
    // and re-emits them as its own --accent/--ring/... custom properties.
    colorPrimary: colors.accent,
    colorPrimaryForeground: colors.accentFg,
    colorTextOnPrimaryBackground: colors.accentFg,
    colorDanger: colors.destructive,
    colorForeground: colors.ink,
    colorMuted: colors.muted,
    colorMutedForeground: colors.inkMuted,
    colorBackground: colors.card,
    colorInput: colors.surface,
    colorInputForeground: colors.ink,
    colorNeutral: colors.ink,
    colorBorder: colors.border,
    colorRing: colors.ring,
    // Deprecated aliases still read by some Clerk UI paths.
    colorText: colors.ink,
    colorTextSecondary: colors.inkMuted,
    colorInputBackground: colors.surface,
    colorInputText: colors.ink,
  }

  const primaryAction = {
    backgroundColor: `${colors.accent} !important`,
    color: `${colors.accentFg} !important`,
  }

  const elements: NonNullable<OrigensClerkAppearance['elements']> = {
    formButtonPrimary: primaryAction,
    membersPageInviteButton: primaryAction,
    rootBox: {
      width: '100%',
    },
    cardBox: {
      width: '100%',
      maxWidth: 'none',
    },
    card: {
      backgroundColor: colors.card,
      border: `1px solid ${colors.border}`,
      boxShadow: 'none',
    },
    navbar: {
      backgroundColor: colors.surface,
      borderRight: `1px solid ${colors.border}`,
    },
    navbarButton: {
      color: colors.inkMuted,
    },
  }

  if (colorScheme === 'dark') {
    return {
      // Cast: @clerk/themes `dark` optional fields conflict with app
      // exactOptionalPropertyTypes vs Clerk's BaseTheme intersection.
      theme: dark as NonNullable<OrigensClerkAppearance['theme']>,
      captcha: { theme: 'dark' },
      variables,
      elements,
    }
  }

  return {
    captcha: { theme: 'light' },
    variables,
    elements,
  }
}
